import { api } from './axios';

export type StudentStatus = 'ACTIVE' | 'SUSPENDED' | 'LEFT' | 'GRADUATED';

export interface StudentSummary {
  id: number;
  registrationNumber: string;
  legacyAdmissionNumber: string | null;
  firstName: string;
  lastName: string;
  currentStatus: StudentStatus;
  currentClassName: string | null;
  currentSectionName: string | null;
  currentClassSectionId: number | null;
  primaryGuardianName: string | null;
  primaryGuardianPhone: string | null;
}

export interface StudentDetail {
  id: number;
  registrationNumber: string;
  legacyAdmissionNumber: string | null;
  firstName: string;
  lastName: string;
  dateOfBirth: string | null;
  genderId: number | null;
  photoPath: string | null;
  bloodGroupId: number | null;
  religionId: number | null;
  nationalityId: number | null;
  addressLine: string | null;
  cityId: number | null;
  currentStatus: StudentStatus;
  statusEffectiveDate: string | null;
  leavingDate: string | null;
  leavingReasonId: number | null;
  emergencyContactName: string | null;
  emergencyContactPhone: string | null;
  emergencyRelation: string | null;
  medicalNotes: string | null;
  prevSchoolName: string | null;
  prevSchoolLeavingClass: string | null;
  prevSchoolTcNumber: string | null;
  createdAt: string;
  guardians: Guardian[];
  currentEnrollment: EnrollmentRow | null;
}

export interface Guardian {
  id: number;
  studentId: number;
  relationshipTypeId: number;
  fullName: string;
  cnic: string | null;
  phone: string | null;
  occupation: string | null;
  primaryContact: boolean;
}

export interface EnrollmentRow {
  id: number;
  studentId: number;
  academicYearId: number;
  academicYearName: string;
  classSectionId: number;
  className: string;
  sectionName: string;
  enrollmentDate: string;
  active: boolean;
}

export interface UpsertStudent {
  firstName: string;
  lastName: string;
  dateOfBirth?: string | null;
  genderId?: number | null;
  photoPath?: string | null;
  bloodGroupId?: number | null;
  religionId?: number | null;
  nationalityId?: number | null;
  addressLine?: string | null;
  cityId?: number | null;
  emergencyContactName?: string | null;
  emergencyContactPhone?: string | null;
  emergencyRelation?: string | null;
  medicalNotes?: string | null;
  prevSchoolName?: string | null;
  prevSchoolLeavingClass?: string | null;
  prevSchoolTcNumber?: string | null;
  legacyAdmissionNumber?: string | null;
}

export interface UpsertGuardian {
  relationshipTypeId: number;
  fullName: string;
  cnic?: string | null;
  phone?: string | null;
  occupation?: string | null;
  primaryContact: boolean;
}

export interface EnrollRequest {
  academicYearId: number;
  classSectionId: number;
  enrollmentDate: string;
  inquiryId?: number | null;
}

export interface TransferSectionRequest {
  classSectionId: number;
  reason?: string | null;
}

export interface BulkImportRow {
  student: UpsertStudent;
  primaryGuardian?: UpsertGuardian | null;
  enrollment?: EnrollRequest | null;
}

export interface RegisterDiscount {
  feeTypeId: number;
  discountTypeId: number;
  percentage: boolean;
  value: string;
  remarks?: string | null;
}

export interface RegisterStudent {
  student: UpsertStudent;
  primaryGuardian: UpsertGuardian;
  enrollment: EnrollRequest;
  discounts?: RegisterDiscount[];
  inquiryId?: number | null;
}

export interface BulkImportResult {
  total: number;
  created: number;
  failed: number;
  outcomes: { rowIndex: number; success: boolean; studentId: number | null; registrationNumber: string | null; error: string | null }[];
}

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

export interface SearchParams {
  q?: string;
  classSectionId?: number;
  academicYearId?: number;
  status?: StudentStatus;
  page?: number;
  size?: number;
  sort?: string;
}

export type BehaviorNoteType = 'POSITIVE' | 'CONCERN' | 'INCIDENT';

export interface BehaviorNote {
  id: number;
  studentId: number;
  noteType: BehaviorNoteType;
  title: string;
  body: string;
  occurredOn: string;
  createdBy: number | null;
  createdByUsername: string | null;
  createdAt: string;
}

export interface UpsertBehaviorNote {
  noteType: BehaviorNoteType;
  title: string;
  body: string;
  occurredOn: string;
}

export const studentsApi = {
  search: (params: SearchParams) =>
    api.get<PageResponse<StudentSummary>>('/students', { params }).then((r) => r.data),
  get: (id: number) => api.get<StudentDetail>(`/students/${id}`).then((r) => r.data),
  create: (body: UpsertStudent) => api.post<StudentDetail>('/students', body).then((r) => r.data),
  register: (body: RegisterStudent) => api.post<StudentDetail>('/students/register', body).then((r) => r.data),
  update: (id: number, body: UpsertStudent) => api.put<StudentDetail>(`/students/${id}`, body).then((r) => r.data),

  changeStatus: (id: number, body: { toStatus: StudentStatus; effectiveDate: string; reason?: string; leavingReasonId?: number }) =>
    api.post<StudentDetail>(`/students/${id}/status`, body).then((r) => r.data),
  statusHistory: (id: number) => api.get(`/students/${id}/status-history`).then((r) => r.data),

  addGuardian:    (id: number, body: UpsertGuardian) => api.post<Guardian>(`/students/${id}/guardians`, body).then((r) => r.data),
  updateGuardian: (id: number, gid: number, body: UpsertGuardian) =>
    api.put<Guardian>(`/students/${id}/guardians/${gid}`, body).then((r) => r.data),
  deleteGuardian: (id: number, gid: number) => api.delete(`/students/${id}/guardians/${gid}`),

  enroll:    (id: number, body: EnrollRequest) => api.post<EnrollmentRow>(`/students/${id}/enroll`, body).then((r) => r.data),
  transferSection: (id: number, body: TransferSectionRequest) =>
    api.post<EnrollmentRow>(`/students/${id}/transfer-section`, body).then((r) => r.data),
  history:   (id: number) => api.get<EnrollmentRow[]>(`/students/${id}/enrollments`).then((r) => r.data),

  bulkImport: (rows: BulkImportRow[]) => api.post<BulkImportResult>('/students/bulk', rows).then((r) => r.data),

  listBehaviorNotes: (id: number) =>
    api.get<BehaviorNote[]>(`/students/${id}/behavior-notes`).then((r) => r.data),
  createBehaviorNote: (id: number, body: UpsertBehaviorNote) =>
    api.post<BehaviorNote>(`/students/${id}/behavior-notes`, body).then((r) => r.data),
  updateBehaviorNote: (noteId: number, body: UpsertBehaviorNote) =>
    api.put<BehaviorNote>(`/students/behavior-notes/${noteId}`, body).then((r) => r.data),
  deleteBehaviorNote: (noteId: number) => api.delete(`/students/behavior-notes/${noteId}`),

  bulkImportCsv: (file: File, academicYear?: string) => {
    const form = new FormData();
    form.append('file', file);
    if (academicYear) form.append('academicYear', academicYear);
    return api
      .post<BulkImportResult>('/students/bulk-csv', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },
};
